import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";
import { useHoneypot } from "@/lib/honeypot";
import MarketingPageHeader from "@/components/MarketingPageHeader";
import {
  Mail,
  Clock,
  MessageCircle,
  LifeBuoy,
} from "lucide-react";

const SUPPORT_EMAIL = "hello@jynio.co";

// Values are the stable codes the backend receives; labels translate at render.
const subjectOptions = [
  { value: "general", labelKey: "contact.subjects.general" },
  { value: "support", labelKey: "contact.subjects.support" },
  { value: "billing", labelKey: "contact.subjects.billing" },
  { value: "tutor", labelKey: "contact.subjects.tutor" },
  { value: "press", labelKey: "contact.subjects.press" },
  { value: "other", labelKey: "contact.subjects.other" },
];

const SUBMIT_CONTACT_FORM = graphql(`
  mutation SubmitContactForm($name: String!, $email: String!, $subject: String!, $message: String!, $website: String, $formElapsedMs: Int) {
    submitContactForm(input: { name: $name, email: $email, subject: $subject, message: $message, website: $website, formElapsedMs: $formElapsedMs }) {
      success
      errors
    }
  }
`);

const Contact = () => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("general");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const honeypot = useHoneypot();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await graphqlClient.request(SUBMIT_CONTACT_FORM, { name, email, subject, message, website: honeypot.website, formElapsedMs: honeypot.elapsedMs() });

      const errs = result.submitContactForm.errors;
      if (errs.length > 0) {
        toast({
          title: t("contact.toast.sendFailedTitle"),
          description: errs.join(", "),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("contact.toast.successTitle"),
        description: t("contact.toast.successDescription"),
      });
      setName("");
      setEmail("");
      setSubject("general");
      setMessage("");
    } catch (err: any) {
      toast({
        title: t("contact.toast.serverErrorTitle"),
        description: err.message || t("contact.toast.serverErrorFallback", { email: SUPPORT_EMAIL }),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <MarketingPageHeader
        containerClassName="max-w-5xl"
        title={t("contact.title")}
        subtitle={t("contact.subtitle")}
      />
      <main className="container mx-auto px-4 pb-24 pt-6 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="block">
            <Card className="h-full shadow-card hover:shadow-hover transition-shadow">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <Mail className="w-5 h-5 text-foreground" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">{t("contact.emailCardTitle")}</h3>
                <p className="text-sm text-muted-foreground mb-2">{t("contact.emailCardBody")}</p>
                <p className="text-sm font-medium text-foreground">{SUPPORT_EMAIL}</p>
              </CardContent>
            </Card>
          </a>

          <Card className="h-full bg-secondary/50">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                <Clock className="w-5 h-5 text-foreground" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">{t("contact.responseCardTitle")}</h3>
              <p className="text-sm text-muted-foreground">{t("contact.responseCardBody")}</p>
            </CardContent>
          </Card>

          <Card className="h-full bg-secondary/50">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                <LifeBuoy className="w-5 h-5 text-foreground" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">{t("contact.helpCardTitle")}</h3>
              <p className="text-sm text-muted-foreground">
                {/* The anchors carry their English text as static children so
                    jsx-a11y sees accessible content; Trans swaps in each
                    locale's <faq>/<help> tag content at render time. */}
                <Trans
                  i18nKey="contact.helpCardBody"
                  components={{
                    faq: <a href="/faq" className="text-foreground underline">FAQ</a>,
                    help: <a href="/help" className="text-foreground underline">Help Center</a>,
                  }}
                />
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <MessageCircle className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-xl font-semibold text-foreground">{t("contact.formTitle")}</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <input {...honeypot.inputProps} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">{t("contact.nameLabel")}</Label>
                  <Input
                    id="contact-name"
                    type="text"
                    placeholder={t("contact.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">{t("contact.emailLabel")}</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    placeholder={t("auth.signIn.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-subject">{t("contact.subjectLabel")}</Label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger id="contact-subject">
                    <SelectValue placeholder={t("contact.subjectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message">{t("contact.messageLabel")}</Label>
                <Textarea
                  id="contact-message"
                  placeholder={t("contact.messagePlaceholder")}
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="text-xs text-muted-foreground">
                  <Trans
                    i18nKey="contact.privacyNote"
                    components={{ privacy: <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a> }}
                  />
                </p>
                <Button type="submit" disabled={submitting}>
                  {submitting ? t("contact.sending") : t("contact.sendButton")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
